import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface ShortcutAction {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: () => void;
  description: string;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();

  useEffect(() => {
    const shortcuts: ShortcutAction[] = [
      { key: 'n', ctrl: true, action: () => navigate('/sales'), description: 'New Sale' },
      { key: 'e', ctrl: true, action: () => navigate('/expenses'), description: 'New Expense' },
      { key: 'r', ctrl: true, action: () => navigate('/reports'), description: 'View Reports' },
      { key: 'd', ctrl: true, action: () => navigate('/dashboard'), description: 'Go to Dashboard' },
      { key: 'c', ctrl: true, shift: true, action: () => navigate('/customers'), description: 'View Customers' },
      { key: 's', ctrl: true, shift: true, action: () => navigate('/services'), description: 'View Services' },
      { key: '/', ctrl: false, action: () => {
        // Focus search - trigger Ctrl+K programmatically
        const event = new KeyboardEvent('keydown', { key: 'k', ctrlKey: true });
        document.dispatchEvent(event);
      }, description: 'Open Search' },
    ];

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return;
      }

      for (const shortcut of shortcuts) {
        const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : !(e.ctrlKey || e.metaKey);
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
        const altMatch = shortcut.alt ? e.altKey : !e.altKey;

        if (e.key.toLowerCase() === shortcut.key && ctrlMatch && shiftMatch && altMatch) {
          // Don't prevent default for browser shortcuts like Ctrl+C, Ctrl+V
          if (shortcut.ctrl && ['c', 'v', 'x', 'a', 'z'].includes(shortcut.key)) {
            continue;
          }
          e.preventDefault();
          shortcut.action();
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Return shortcuts list for help display
  return {
    shortcuts: [
      { keys: 'Alt + G', description: 'Open Calculator' },
      { keys: 'Ctrl + N', description: 'New Sale' },
      { keys: 'Ctrl + E', description: 'New Expense' },
      { keys: 'Ctrl + R', description: 'View Reports' },
      { keys: 'Ctrl + D', description: 'Dashboard' },
      { keys: 'Ctrl + Shift + C', description: 'Customers' },
      { keys: 'Ctrl + Shift + S', description: 'Services' },
      { keys: 'Ctrl + K', description: 'Quick Search' },
      { keys: '/', description: 'Focus Search' },
    ],
  };
}
